import * as Sentry from '@sentry/react'
import Cookies from 'js-cookie'
import { v4 as uuidv4 } from 'uuid'
import type { EventName } from '../types'
import detectPlatform from './detectPlatform'
import getClvCurrencyAndValue from './getClvCurrencyAndValue'
import { hashValue } from './hashValue'
import {
  FACEBOOK_CLICK_ID_KEY,
  getAgeFromLocalStorage,
  getFacebookPixelId,
} from './localStorage'
import {
  getUserCity,
  getUserCountryCode,
  getUserEmail,
  getUserId,
  getUserZipCode,
} from './sessionStorage'
import { trackEvent } from './supabase'

export const CUSTOM_PURCHASE_EVENT_KEY = 'custom_purchase_event_name'

const STANDARD_EVENTS: Set<string> = new Set<string>([
  'CompleteRegistration',
  'InitiateCheckout',
  'Purchase',
])

type SubscriptionEventType = 'initiate-checkout' | 'purchase'

const STRIPE_URL_PARAMS_TO_REMOVE = new Set([
  'payment_intent',
  'redirect_status',
  'setup_intent',
])

export async function trackCompleteRegistrationEvent() {
  const eventId = createEventId()
  const eventName = 'CompleteRegistration'

  await Promise.all([
    trackEventFromServer({ eventId, eventName }),
    trackEventFromClient({ eventId, eventName }),
  ])
}

export async function trackSubscriptionEvent({
  currency,
  customPurchaseEventName,
  eventType,
  stripeFirstPriceId,
  stripeNextPriceId,
}: {
  currency: string
  customPurchaseEventName?: string
  eventType: SubscriptionEventType
  stripeFirstPriceId: string
  stripeNextPriceId: string
}) {
  removeSensitiveStripeParamsFromBrowserUrl()

  const age = getAgeFromLocalStorage()
  const os = detectPlatform()

  const { clvCurrency, clvValue } = await getClvCurrencyAndValue({
    age,
    currency,
    os,
    stripeFirstPriceId,
    stripeNextPriceId,
  })

  const eventId = createEventId()
  let eventName: EventName

  switch (eventType) {
    case 'initiate-checkout':
      eventName = 'InitiateCheckout'
      break
    case 'purchase':
      eventName = customPurchaseEventName ?? 'Purchase'
      break
    default: {
      const exhaustiveCheck: never = eventType
      throw new Error(`Unhandled subscription event: ${exhaustiveCheck}`)
    }
  }

  await Promise.all([
    trackEventFromServer({
      currency: clvCurrency,
      eventId,
      eventName,
      value: clvValue,
    }),
    trackEventFromClient({
      currency: clvCurrency,
      eventId,
      eventName,
      value: clvValue,
    }),
  ])
}

function createEventId(): string {
  return uuidv4()
}

function getSanitizedEventSourceUrl(): string {
  const url = new URL(window.location.href)

  for (const key of Array.from(url.searchParams.keys())) {
    if (
      key === 'client_secret' ||
      key.endsWith('_client_secret') ||
      STRIPE_URL_PARAMS_TO_REMOVE.has(key)
    ) {
      url.searchParams.delete(key)
    }
  }

  return url.toString()
}

function removeSensitiveStripeParamsFromBrowserUrl(): void {
  const sanitizedUrl = getSanitizedEventSourceUrl()

  if (sanitizedUrl === window.location.href) return

  const url = new URL(sanitizedUrl)
  window.history.replaceState(
    window.history.state,
    '',
    `${url.pathname}${url.search}${url.hash}`,
  )
}

async function trackEventFromClient({
  currency,
  eventId,
  eventName,
  value,
}: {
  currency?: string
  eventId: string
  eventName: EventName
  value?: number
}) {
  const userCity = await getUserCity()
  const userCountryCode = (await getUserCountryCode()).toLowerCase()
  const userEmail = await getUserEmail()
  const userId = await getUserId()
  const userZipCode = await getUserZipCode()

  // Pre-hash external_id (RC user UUID) on the client so the pixel
  // sends the same SHA-256 we send through CAPI in
  // `trackEventFromServer` and through mobile CAPI / App Events from
  // app-studio-supabase. Meta's pixel auto-hashes em / ct / zp /
  // country with their own normalization, but external_id behaves
  // inconsistently — pre-hashing makes it deterministic so a single
  // user produces the same external_id hash on every Meta surface,
  // which is the prerequisite for cross-channel match-rate gains.
  // Other fields stay raw and let Meta auto-hash them as today.
  const hashedUserId = await hashValue(userId)

  const payload =
    eventName === 'CompleteRegistration' ? {} : { currency, value }

  return new Promise<void>((resolve, reject) => {
    if (fbq) {
      fbq('init', getFacebookPixelId(import.meta.env.VITE_FACEBOOK_PIXEL_ID), {
        country: userCountryCode,
        ct: userCity,
        em: userEmail,
        external_id: hashedUserId,
        zp: userZipCode,
      })

      const trackMethod = STANDARD_EVENTS.has(eventName)
        ? 'track'
        : 'trackCustom'
      fbq(trackMethod, eventName, payload, {
        eventID: eventId,
      })

      resolve()
    } else {
      Sentry.captureException('Facebook Pixel not loaded')
      reject(new Error('Facebook Pixel not loaded'))
    }
  })
}

async function trackEventFromServer({
  currency,
  eventId,
  eventName,
  value,
}: {
  currency?: string
  eventId: string
  eventName: EventName
  value?: number
}) {
  const eventSourceUrl = getSanitizedEventSourceUrl()
  const fbcFromCookie = Cookies.get('_fbc')
  const fbcFromLocalStorage = localStorage.getItem(FACEBOOK_CLICK_ID_KEY)
  const fbc = fbcFromCookie ?? fbcFromLocalStorage
  const fbp = Cookies.get('_fbp')
  const userCity = await getUserCity()
  const userCountryCode = (await getUserCountryCode()).toLowerCase()
  const userEmail = await getUserEmail()
  const userId = await getUserId()
  const userZipCode = await getUserZipCode()

  await trackEvent({
    appId: import.meta.env.VITE_SUPABASE_APP_ID as string,
    currency,
    eventId,
    eventName,
    eventSourceUrl,
    fbc,
    fbcFromCookie,
    fbcFromLocalStorage,
    fbp,
    pixelId: getFacebookPixelId(
      import.meta.env.VITE_FACEBOOK_PIXEL_ID as string,
    ),
    userCity: await hashValue(userCity),
    userCountryCode: await hashValue(userCountryCode),
    userEmail: await hashValue(userEmail),
    userId: await hashValue(userId),
    userZipCode: await hashValue(userZipCode),
    value,
  })
}
